/**
 * AURA Reading Aids Effects
 * Read Mode, Reading Guide, and Reading Mask implementations.
 * Supports both mouse and touch input so the ADHD profile and
 * manual toggles work on desktop AND mobile devices.
 */

import { cssInjector } from '../../core/css-injector';
import { Logger } from '../../core/logger';
import {
  addPointerMoveListeners,
  PointerCoords,
} from '../../core/pointer-utils';

const logger = new Logger('ReadingAids');

const STYLE_ID = 'read-mode';

const AURA_GUARD = ':not([data-aura])';

const HIDE_SELECTORS = [
  '[role="complementary"]', '[role="contentinfo"]',
  '.ad', '.ads', '.advertisement',
  '.cookie-banner', '.cookie-consent', '.cookie-notice',
  '[id*="cookie-banner"]', '[id*="cookie-consent"]',
] as const;

const LAYOUT_PARENT_SELECTORS = [
  '[role="complementary"]',
] as const;

const CONTENT_SELECTORS = [
  'main', 'article', '[role="main"]',
  `.content${AURA_GUARD}`, '#content',
] as const;

let readingGuideElement: HTMLElement | null = null;
let readingMaskElement: HTMLElement | null = null;
let removeGuideListeners: (() => void) | null = null;
let removeMaskListeners: (() => void) | null = null;
let removeMaskHandleListeners: (() => void) | null = null;

// Where the reading mask is mounted.
//
// It MUST be <html> (document.documentElement), NOT <body>.  Proven by a
// live probe against Beacon: Beacon sets `filter: saturate(0.5)` on <body>,
// and any non-`none` `filter` (also `transform`, `contain`, `will-change`,
// `perspective`, `backdrop-filter`) makes that element the containing block
// for EVERY `position: fixed` descendant.  A mask under <body> therefore
// anchors to the body box (full document height) instead of the viewport —
// the bottom dim lands off-screen and the whole mask scrolls with the page.
// <html> has no such trigger on Beacon (and virtually never does), so a mask
// mounted there gets true viewport-anchored fixed positioning.
//
// A setter allows tests / unusual embeddings to override the target; the
// default is computed lazily so it works even if <html> appears after load.
let maskMountTarget: HTMLElement | null = null;

/**
 * Override the DOM node the reading mask mounts into.  Pass null to use the
 * default (`document.documentElement`).  Exposed mainly for tests.
 */
export function setReadingMaskMount(target: HTMLElement | null): void {
  maskMountTarget = target;
}

function getMaskMountTarget(): HTMLElement | null {
  return maskMountTarget ?? document.documentElement ?? document.body;
}

// Baseline strip height in unscaled pixels.  Recomputed per enable as
// READING_MASK_HEIGHT so admin UIs that shrink their content (Beacon
// uses CSS `zoom: 0.875`) get a proportionally smaller strip instead
// of a fixed 120 px that visually engulfs the shrunken text.
const READING_MASK_HEIGHT_BASE = 120;
let READING_MASK_HEIGHT = READING_MASK_HEIGHT_BASE;

// Initial position of the strip's bottom edge on enable: ~40 % down the
// viewport, so the strip sits in the upper reading zone instead of dead
// centre (which would block the most-read line).
const INITIAL_STRIP_BOTTOM_RATIO = 0.4;

// The reading guide stays in the light DOM (a thin line, never reported
// broken).  The mask can live under the shadow mount OR document.body, so
// check membership against the whole document tree, not just body.
function isInDOM(el: HTMLElement | null): boolean {
  return !!el && el.isConnected;
}

export function buildReadModeCSS(): string {
  const hide = HIDE_SELECTORS
    .map(s => `:where(html) body ${s}${AURA_GUARD}`)
    .join(',\n        ');

  const layoutParents = LAYOUT_PARENT_SELECTORS
    .map(s => `:where(html) body *:has(> ${s}${AURA_GUARD})`)
    .join(',\n        ');

  const content = CONTENT_SELECTORS
    .map(s => `:where(html) body ${s}`)
    .join(',\n        ');

  return `
        ${hide} {
          display: none !important;
        }

        :where(html), :where(html) body {
          overflow-y: auto !important;
          height: auto !important;
        }

        ${layoutParents} {
          display: block !important;
          overflow-y: auto !important;
        }

        ${content} {
          max-width: 720px !important;
          width: 100% !important;
          margin-left: auto !important;
          margin-right: auto !important;
          padding: 2rem 1rem !important;
          box-sizing: border-box !important;
          font-family: Georgia, 'Times New Roman', serif !important;
          font-size: 1.125rem !important;
          line-height: 1.8 !important;
        }
  `;
}

export function applyReadMode(enabled: boolean): void {
  if (enabled) {
    cssInjector.setStylesheet(STYLE_ID, buildReadModeCSS(), 199);
    logger.debug('Read mode enabled');
  } else {
    cssInjector.removeStylesheet(STYLE_ID);
    logger.debug('Read mode disabled');
  }
}

export function applyReadingGuide(enabled: boolean): void {
  if (enabled) {
    enableReadingGuide();
  } else {
    disableReadingGuide();
  }
}

export function applyReadingMask(enabled: boolean): void {
  if (enabled) {
    enableReadingMask();
  } else {
    disableReadingMask();
  }
}

export function destroyReadingAids(): void {
  disableReadingGuide();
  disableReadingMask();
  cssInjector.removeStylesheet(STYLE_ID);
}

function enableReadingGuide(): void {
  if (readingGuideElement && isInDOM(readingGuideElement)) return;
  disableReadingGuide();

  const appendTarget = document.body ?? document.documentElement;
  if (!appendTarget) {
    logger.warn('No DOM target available for reading guide');
    return;
  }

  readingGuideElement = document.createElement('div');
  readingGuideElement.setAttribute('data-aura', 'reading-guide');
  readingGuideElement.style.cssText = `
    position: fixed; left: 0; right: 0; height: 8px;
    background: rgba(74, 144, 217, 0.4);
    pointer-events: none; z-index: 999997;
    transition: top 0.05s ease-out;
  `;
  appendTarget.appendChild(readingGuideElement);

  removeGuideListeners = addPointerMoveListeners(
    document,
    handleReadingGuideMove,
  );
  logger.debug('Reading guide enabled');
}

function disableReadingGuide(): void {
  if (readingGuideElement) {
    readingGuideElement.remove();
    readingGuideElement = null;
  }
  removeGuideListeners?.();
  removeGuideListeners = null;
}

function handleReadingGuideMove(coords: PointerCoords): void {
  if (!readingGuideElement || !isInDOM(readingGuideElement)) return;
  readingGuideElement.style.top = `${coords.clientY - 4}px`;
}

/**
 * Detect the effective rendering scale of the host page's content area.
 *
 * Returns 1 when the host renders at 1:1, or the scale factor
 * (clamped to [0.5, 2]) when CSS `zoom` or `transform: scale` shrinks
 * or enlarges a content wrapper.  The reading mask multiplies its
 * strip height by this value so a `zoom: 0.875` host like Beacon gets
 * a strip ~105 px tall instead of 120 px — visually proportional to
 * the shrunken text.
 *
 * Two strategies, in order:
 *   1. Read `getComputedStyle(...).zoom` on common content wrappers.
 *      This is the most reliable signal because `zoom` is exactly the
 *      property Beacon uses.
 *   2. Compare a sample paragraph's bounding-box width against its CSS
 *      width — catches hosts that use `transform: scale` instead of
 *      `zoom`, which produces the same visual effect but is invisible
 *      to the `zoom` check.
 */
function detectContentScale(): number {
  if (typeof document === 'undefined') return 1;
  const clamp = (n: number) => Math.max(0.5, Math.min(2, n));

  for (const sel of ['main', 'article', '[role="main"]']) {
    const el = document.querySelector(sel);
    if (!el) continue;
    const ownZoom = parseFloat(window.getComputedStyle(el).zoom);
    if (Number.isFinite(ownZoom) && ownZoom > 0 && ownZoom !== 1) {
      return clamp(ownZoom);
    }
    for (const child of Array.from(el.children)) {
      const childZoom = parseFloat(window.getComputedStyle(child).zoom);
      if (Number.isFinite(childZoom) && childZoom > 0 && childZoom !== 1) {
        return clamp(childZoom);
      }
    }
  }

  for (const sel of ['main p', 'article p', '[role="main"] p']) {
    const sample = document.querySelector(sel) as HTMLElement | null;
    if (!sample) continue;
    const rect = sample.getBoundingClientRect();
    if (rect.width <= 0) continue;
    const cssWidth = parseFloat(window.getComputedStyle(sample).width);
    if (!Number.isFinite(cssWidth) || cssWidth <= 0) continue;
    const ratio = rect.width / cssWidth;
    if (Number.isFinite(ratio) && ratio > 0 && Math.abs(ratio - 1) > 0.02) {
      return clamp(ratio);
    }
  }

  return 1;
}

function enableReadingMask(): void {
  if (readingMaskElement && isInDOM(readingMaskElement)) return;
  disableReadingMask();

  const appendTarget = getMaskMountTarget();
  if (!appendTarget) {
    logger.warn('No DOM target available for reading mask');
    return;
  }

  // Resize the strip to match the host's content scale before any
  // painting happens.  Subsequent mouse-move / drag calls all read
  // READING_MASK_HEIGHT, so the scale only needs to be applied once.
  READING_MASK_HEIGHT = Math.max(
    20,
    Math.round(READING_MASK_HEIGHT_BASE * detectContentScale()),
  );

  readingMaskElement = document.createElement('div');
  readingMaskElement.setAttribute('data-aura', 'reading-mask');
  readingMaskElement.innerHTML = `
    <div class="aura-mask-top"></div>
    <div class="aura-mask-bottom"></div>
    <div data-aura="reading-mask-handle" class="aura-mask-handle"
         role="slider" aria-label="Reading mask position"
         aria-orientation="vertical">
      <span class="aura-mask-grip"></span>
    </div>
  `;
  readingMaskElement.style.cssText = `
    position: fixed; inset: 0;
    pointer-events: none; z-index: 999996;
  `;

  const style = document.createElement('style');
  // Mask CSS — three rules + ONE touch-only @media to reveal the handle.
  // Keeping the wrapper at `position: fixed; inset: 0` and the children at
  // `position: absolute` means the children anchor to the wrapper, which
  // is the viewport — the simple, original layout the user said worked
  // everywhere before.  The handle is hidden by default and revealed only
  // on devices that report any coarse pointer (phones, tablets, S-Pen
  // phones with touchscreens) so the desktop mouse-follow experience is
  // unchanged.
  style.textContent = `
    [data-aura="reading-mask"] .aura-mask-top,
    [data-aura="reading-mask"] .aura-mask-bottom {
      position: absolute; left: 0; right: 0;
      background: rgba(0, 0, 0, 0.6);
      transition: all 0.05s ease-out;
    }
    [data-aura="reading-mask"] .aura-mask-top { top: 0; height: 40%; }
    [data-aura="reading-mask"] .aura-mask-bottom { bottom: 0; height: 40%; }

    [data-aura="reading-mask"] .aura-mask-handle {
      position: absolute;
      left: 50%;
      top: 60%;
      width: 96px;
      height: 32px;
      margin-left: -48px;
      margin-top: -16px;
      background: rgba(20, 20, 20, 0.92);
      border-radius: 999px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
      cursor: ns-resize;
      pointer-events: auto;
      touch-action: none;
      display: none;
      align-items: center;
      justify-content: center;
      transition: top 0.05s ease-out;
    }
    [data-aura="reading-mask"] .aura-mask-grip {
      width: 32px; height: 4px; border-radius: 2px;
      background: rgba(255, 255, 255, 0.85);
    }
    @media (any-pointer: coarse) {
      [data-aura="reading-mask"] .aura-mask-handle { display: flex; }
    }
  `;
  readingMaskElement.appendChild(style);
  appendTarget.appendChild(readingMaskElement);

  removeMaskListeners = addPointerMoveListeners(
    document,
    handleReadingMaskMove,
  );
  const handleEl = readingMaskElement.querySelector(
    '[data-aura="reading-mask-handle"]',
  ) as HTMLElement;
  removeMaskHandleListeners = attachHandleDrag(handleEl);

  // Paint immediately in px so the strip is correctly sized the instant the
  // mask is enabled.  Critical on touch, where there is no mousemove to
  // trigger the first paint — without this the dim layers stay at the 40%
  // CSS default until the user drags the handle.
  paintMaskAtStripBottom(window.innerHeight * INITIAL_STRIP_BOTTOM_RATIO);

  logger.debug('Reading mask enabled');
}

function disableReadingMask(): void {
  if (readingMaskElement) {
    readingMaskElement.remove();
    readingMaskElement = null;
  }
  removeMaskListeners?.();
  removeMaskListeners = null;
  removeMaskHandleListeners?.();
  removeMaskHandleListeners = null;
}

/**
 * Re-paint the mask so the strip's bottom edge sits at viewport
 * y = stripBottomY.  Single source of truth for positioning math —
 * both the desktop mouse-follow path and the touch-drag path call this.
 */
function paintMaskAtStripBottom(stripBottomY: number): void {
  if (!readingMaskElement || !isInDOM(readingMaskElement)) return;
  const vh = window.innerHeight;
  const topHeight = Math.max(0, stripBottomY - READING_MASK_HEIGHT);
  const bottomHeight = Math.max(0, vh - stripBottomY);
  const topMask = readingMaskElement.querySelector('.aura-mask-top') as HTMLElement;
  const bottomMask = readingMaskElement.querySelector('.aura-mask-bottom') as HTMLElement;
  const handle = readingMaskElement.querySelector(
    '[data-aura="reading-mask-handle"]',
  ) as HTMLElement | null;
  if (topMask) topMask.style.height = `${topHeight}px`;
  if (bottomMask) bottomMask.style.height = `${bottomHeight}px`;
  // The handle sits AT the bottom edge of the strip (not in the middle),
  // because a centred handle obscures the very text the user is reading.
  if (handle) handle.style.top = `${stripBottomY}px`;
}

function handleReadingMaskMove(coords: PointerCoords): void {
  // Mouse path: cursor sits at the centre of the strip, so the strip's
  // bottom edge is half a strip-height below the cursor.
  paintMaskAtStripBottom(coords.clientY + READING_MASK_HEIGHT / 2);
}

/**
 * Drag interaction for the mobile handle.  Pointer Events provide one
 * unified path for touch + stylus + mouse; capturing the pointer at
 * pointerdown means the move still resolves even when the user's
 * finger leaves the handle's bounding box during a drag.
 *
 * On every move the strip's bottom edge follows the pointer's clientY,
 * so the user is grabbing the bottom of the strip and dragging it.
 */
function attachHandleDrag(handle: HTMLElement): () => void {
  let activePointerId: number | null = null;

  const onMove = (e: PointerEvent) => {
    if (activePointerId === null || e.pointerId !== activePointerId) return;
    paintMaskAtStripBottom(e.clientY);
  };

  const onUp = (e: PointerEvent) => {
    if (activePointerId === null || e.pointerId !== activePointerId) return;
    activePointerId = null;
    document.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerup', onUp);
    document.removeEventListener('pointercancel', onUp);
    if (typeof handle.releasePointerCapture === 'function') {
      try { handle.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    }
  };

  const onDown = (e: PointerEvent) => {
    activePointerId = e.pointerId;
    if (typeof handle.setPointerCapture === 'function') {
      try { handle.setPointerCapture(e.pointerId); } catch { /* noop */ }
    }
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    paintMaskAtStripBottom(e.clientY);
  };

  handle.addEventListener('pointerdown', onDown);

  return () => {
    handle.removeEventListener('pointerdown', onDown);
    document.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerup', onUp);
    document.removeEventListener('pointercancel', onUp);
    activePointerId = null;
  };
}
